import type { EmailSearchResult, ProcessedEmailInfo } from '@/types/emailSearch'
import type { FieldDefinition, FilterCondition, FilterGroup, FilterNode } from '@/tableControls'
import { filterRows, matchTerm } from '@/tableControls'
import { foldForSearch } from '@/utils/foldForSearch'

export function getEmailRowSearchableText(row: EmailSearchResult): string {
  return [row.subject, row.from_address, row.inbox, row.snippet]
    .filter(Boolean)
    .map(String)
    .join(' ')
}

export function filterEmailRowsByText(
  rows: EmailSearchResult[],
  query: string
): EmailSearchResult[] {
  const trimmed = query.trim()
  if (!trimmed) return rows
  const foldedQuery = foldForSearch(trimmed)
  return rows.filter((row) =>
    matchTerm(foldForSearch(getEmailRowSearchableText(row)), foldedQuery)
  )
}

export function getEmailDocReviewStatus(
  row: EmailSearchResult,
  processedInfo: Record<string, ProcessedEmailInfo>
): 'new' | 'processed' {
  return processedInfo[row.message_id] ? 'processed' : 'new'
}

export function getEmailFilterValue(
  row: EmailSearchResult,
  fieldKey: string,
  processedInfo: Record<string, ProcessedEmailInfo>,
  fields: FieldDefinition<EmailSearchResult>[]
): unknown {
  if (fieldKey === 'doc_review_status') {
    return getEmailDocReviewStatus(row, processedInfo)
  }
  const field = fields.find((entry) => entry.key === fieldKey)
  if (field?.accessor) {
    return field.accessor(row)
  }
  return row[fieldKey as keyof EmailSearchResult]
}

export function filterEmailRowsWithTree(
  rows: EmailSearchResult[],
  filterTree: FilterGroup | null,
  fields: FieldDefinition<EmailSearchResult>[],
  processedInfo: Record<string, ProcessedEmailInfo>
): EmailSearchResult[] {
  return filterRows(rows, filterTree, fields, (row, fieldKey) =>
    getEmailFilterValue(row, fieldKey, processedInfo, fields)
  )
}

function walkFilterNodes(
  node: FilterNode,
  visit: (condition: FilterCondition) => void
): void {
  if (node.kind === 'condition') {
    visit(node)
    return
  }
  for (const child of node.children) {
    walkFilterNodes(child, visit)
  }
}

/** True when the filter tree includes Doc Review status = New only. */
export function isEmailNewOnlyStatusFilter(filterTree: FilterGroup | null): boolean {
  if (!filterTree) return false
  let found = false
  walkFilterNodes(filterTree, (condition) => {
    if (
      condition.field === 'doc_review_status' &&
      condition.operator === 'equals' &&
      condition.value === 'new'
    ) {
      found = true
    }
  })
  return found
}
